import ApiService from "./ApiService";
import appConfig from "configs/app.config";
import { getCookie } from "./CommonService";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosQuery, baseQuery } from "./basequery";
import { Environment, getEnvironment } from "utils/getEnvironment";

const servicePrefix = "/engine";

const csrftoken = getCookie("csrftoken");

export const projectService = createApi({
  reducerPath: "projectApi",
  baseQuery,
  // refetchOnFocus: true,
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "datasets",
    "project",
    "genesis",
    "agents",
    "single-genesis",
    "sessions",
    "agent",
  ],
  endpoints: (build) => ({
    createProject: build.mutation({
      query: ({ ...values }) => ({
        url: `${servicePrefix}/project/`,
        method: "post",
        data: { ...values },
      }),
      invalidatesTags: [{ type: "project", id: "LIST" }],
    }),

    getEngineStart: build.query({
      query: () => ({
        url: `${servicePrefix}/start`,
      }),
    }),

    patchProject: build.mutation({
      query: ({ projectId, ...values }) => ({
        url: `/engine/project/${projectId}/`,
        method: "patch",
        data: { ...values },
      }),
      invalidatesTags: ["project"],
    }),

    getProjectList: build.query({
      query: (data) => {
        let url = data?.search
          ? `${servicePrefix}/projects/${data?.search}`
          : `${servicePrefix}/project/`;
        return {
          url,
          method: "get",
          params: { page: data?.page },
        };
      },
      providesTags: (data) =>
        data?.results
          ? [
              ...data?.results?.map?.(({ id }) => ({ type: "project", id })),
              { type: "project", id: "LIST" },
            ]
          : [{ type: "project", id: "LIST" }],
    }),

    getProjects: build.query({
      query: () => ({
        url: `${servicePrefix}/project/list/`,
      }),
    }),

    getJoinedCompetitions: build.query({
      query: () => ({
        url: `/competitions/user/joined-competitions/`,
      }),
    }),

    getProjectListNoPage: build.query({
      query: (data) => {
        let url = `${servicePrefix}/project/list/`;
        return {
          url,
          method: "get",
          // params: { page: data?.page },
        };
      },
      providesTags: (data) =>
        data?.results
          ? [
              ...data?.results?.map?.(({ id }) => ({ type: "project", id })),
              { type: "project", id: "LIST" },
            ]
          : [{ type: "project", id: "LIST" }],
    }),

    deleteProject: build.mutation({
      query: ({ ...values }) => ({
        url: `${servicePrefix}/project/${values?.app_id}/`,
        method: "delete",
      }),
      invalidatesTags: [{ type: "project", id: "LIST" }],
    }),

    getDashboardStats: build.query({
      query: () => ({
        url: `${servicePrefix}/dashboard/`,
        method: "get",
      }),
    }),

    getProject: build.query({
      query: ({ id }) => ({
        url: `${servicePrefix}/project/${id}/`,
        method: "get",
      }),
      providesTags: (result, error, id) => [{ type: "project", id }],
    }),

    executeBlock: build.mutation({
      query: ({ ...values }) => ({
        url: `${servicePrefix}/start`,
        method: "post",
        data: { ...defaultData, ...values },
        header: { "X-CSRFToken": csrftoken },
      }),
    }),

    getBlocksList: build.query({
      query: () => ({
        url: `${servicePrefix}/frontendmenu`,
        method: "get",
        header: { "X-CSRFToken": csrftoken },
      }),
    }),

    createDataset: build.mutation({
      query: ({ ...values }) => ({
        url: `${servicePrefix}/datasets/`,
        method: "post",
        data: { ...values },
      }),
    }),

    getDatasets: build.query({
      query: () => ({
        url: `${servicePrefix}/datasets`,
        method: "get",
      }),
    }),

    getDatasetsList: build.query({
      query: () => ({
        url: `${servicePrefix}/datasets/list/`,
        method: "get",
      }),
    }),

    getChartDetails: build.query({
      query: () => ({
        url: `${servicePrefix}/chart/`,
        method: "get",
      }),
    }),

    upload: build.mutation({
      query: ({ ...values }) => ({
        url: `${servicePrefix}/upload/`,
        method: "post",
        data: { ...values },
      }),
    }),

    getVisualize: build.mutation({
      query: ({ ...values }) => ({
        url: `${servicePrefix}/visualize/`,
        method: "post",
        data: { ...values },
      }),
    }),

    submitProject: build.mutation({
      query: ({ id, ...values }) => ({
        url: `/competitions/submission/${id}/`,
        method: "post",
        data: { ...values },
      }),
    }),

    postStateManagement: build.mutation({
      query: ({ ...values }) => ({
        url: `${servicePrefix}/statemanagement`,
        method: "post",
        data: { ...values },
      }),
    }),

    patchFlowJson: build.mutation({
      query: ({ projectId, ...data }) => {
        return {
          url: `${servicePrefix}/flowy-json/${projectId}/`,
          method: "patch",
          data: { ...data },
        };
      },
    }),

    patchPipeFlowJson: build.mutation({
      query: ({ projectId, ...data }) => {
        return {
          url: `${servicePrefix}/pipe/flowy-json/${projectId}/`,
          method: "patch",
          data: { ...data },
        };
      },
    }),

    getFlowJson: build.query({
      query: ({ projectId }) => ({
        url: `${servicePrefix}/flowy-json/${projectId}/`,
        method: "get",
      }),
    }),

    getPipeFlowJson: build.query({
      query: ({ projectId }) => ({
        url: `${servicePrefix}/flowy-json/${projectId}/`,
        method: "get",
      }),
    }),

    getProjectStateManagement: build.query({
      query: ({ project_id, block_id }) => {
        return {
          url: `${servicePrefix}/statemanagement/${project_id}/`,
          params: { id: block_id },
        };
      },
    }),

    getPipelinesFunctions: build.query({
      query: () => {
        return {
          url: `${servicePrefix}/functionsdb?on_pipeline=1`,
        };
      },
    }),

    getProjectPipelines: build.query({
      query: ({ projectId }) => ({
        url: `/models/pipe/${projectId}/`,
      }),
    }),

    createPipeline: build.mutation({
      query: ({ projectId, body }) => ({
        url: `/models/pipe/${projectId}/`,
        data: body,
        method: "post",
      }),
    }),

    patchPipeline: build.mutation({
      query: ({ ...data }) => ({
        url: `/models/pipe/`,
        method: "patch",
        data,
      }),
    }),

    deletePipeline: build.mutation({
      query: ({ pipe_id, projectId }) => ({
        url: `/models/pipe/${projectId}`,
        method: "delete",
        data: { pipe_id },
      }),
    }),

    getGenesisServices: build.query({
      query: () => ({
        url: `/genesys/agent/`,
      }),
      providesTags: (data) =>
        data?.data
          ? [
              ...data?.data?.map?.(({ id }) => ({ type: "genesis", id })),
              { type: "genesis", id: "LIST" },
            ]
          : [{ type: "genesis", id: "LIST" }],
    }),

    getGenesisService: build.query({
      query: ({ serviceId }) => ({
        url: `/genesys/agent/${serviceId}`,
      }),
      providesTags: (data) => [{ type: "agent", id: data?.data?.id }],
    }),

    createGenesisService: build.mutation({
      query: ({ body }) => ({
        url: `/genesys/agent/`,
        data: body,
        method: "post",
      }),
      invalidatesTags: ["genesis"],
    }),

    patchGenesisService: build.mutation({
      query: ({ body, sessionId }) => ({
        url: `/genesys/agent/${sessionId}/`,
        data: body,
        method: "patch",
      }),
      invalidatesTags: ["single-genesis"],
    }),

    updateGenesisService: build.mutation({
      query: ({ body, sessionId }) => ({
        url: `/update-session/${sessionId}/`,
        data: body,
        method: "patch",
      }),
      invalidatesTags: ["genesis"],
    }),

    deleteGenesisService: build.mutation({
      query: ({ serviceId }) => ({
        url: `/genesys/agent/${serviceId}/`,
        method: "delete",
      }),
      invalidatesTags: ["genesis"],
    }),

    getGenesisSessions: build.query({
      query: ({ serviceId }) => ({
        url: `/genesys/chat-session/${serviceId}/`,
      }),
      providesTags: (data) =>
        data?.data
          ? [
              ...data?.data?.map?.(({ id }) => ({ type: "sessions", id })),
              { type: "sessions", id: "LIST" },
            ]
          : [{ type: "sessions", id: "LIST" }],
    }),

    getGenesisAgent: build.query({
      query: ({ serviceId }) => ({
        url: `/genesys/agent-tools/${serviceId}/`,
      }),
      providesTags: ["agents"],
    }),

    addGenesisAgents: build.mutation({
      query: ({ body, serviceId }) => ({
        url: `/genesys/agent-tools/${serviceId}/`,
        data: body,
        method: "post",
      }),
      invalidatesTags: ["agents"],
    }),

    patchGenesisAgents: build.mutation({
      query: ({ body, serviceId }) => ({
        url: `/gensys/service-agent/${serviceId}/`,
        data: body,
        method: "patch",
      }),
      invalidatesTags: ["agent"],
    }),

    sendHumanMessage: build.mutation({
      query: ({ messageId, response }) => ({
        url: `/genesys/reply/${messageId}/`,
        data: { response },
        method: "post",
      }),
      invalidatesTags: ["sessions"],
    }),

    createDemoChatbot: build.mutation({
      query: ({ body, serviceId }) => ({
        url: `/genesys/chat/${serviceId}/`,
        data: body,
        method: "post",
      }),
      invalidatesTags: ["sessions"],
    }),
  }),
});

export const {
  useGetEngineStartQuery,
  useCreateDatasetMutation,
  useGetDashboardStatsQuery,
  useGetChartDetailsQuery,
  useGetProjectListQuery,
  useGetProjectListNoPageQuery,
  useCreateProjectMutation,
  useGetProjectQuery,
  useLazyGetProjectQuery,
  useDeleteProjectMutation,
  useGetVisualizeMutation,
  usePostStateManagementMutation,
  usePatchFlowJsonMutation,
  useGetProjectStateManagementQuery,
  useLazyGetProjectStateManagementQuery,
  useGetPipelinesFunctionsQuery,
  useGetFlowJsonQuery,
  useLazyGetFlowJsonQuery,
  usePatchProjectMutation,
  useGetProjectPipelinesQuery,
  usePatchPipeFlowJsonMutation,
  useGetPipeFlowJsonQuery,
  useCreatePipelineMutation,
  useDeletePipelineMutation,
  usePatchPipelineMutation,
  useGetBlocksListQuery,
  useGetProjectsQuery,
  useExecuteBlockMutation,
  useGetJoinedCompetitionsQuery,
  useSubmitProjectMutation,
  useGetGenesisServicesQuery,
  useCreateGenesisServiceMutation,
  useDeleteGenesisServiceMutation,
  useGetGenesisSessionsQuery,
  useGetGenesisAgentQuery,
  useAddGenesisAgentsMutation,
  usePatchGenesisAgentsMutation,
  useCreateDemoChatbotMutation,
  useUpdateGenesisServiceMutation,
  useSendHumanMessageMutation,
  usePatchGenesisServiceMutation,
  useGetGenesisServiceQuery,
} = projectService;

export const selectProjectState =
  projectService.endpoints.getProjectStateManagement.select();
export const selectProjectFlowState =
  projectService.endpoints.getFlowJson.select();

export async function apiCreateProject(data) {
  return ApiService.fetchData({
    url: servicePrefix + "/project/",
    method: "post",
    data,
  });
}

export async function apiListProjects() {
  return ApiService.fetchData({
    url: servicePrefix + "/project/",
    method: "get",
  });
}

export async function apiGetStats() {
  return ApiService.fetchData({
    url: servicePrefix + "/dashboard/",
    method: "get",
  });
}
export async function apiGetProject(id) {
  return ApiService.fetchData({
    url: servicePrefix + `/project/${id}/`,
    method: "get",
  });
}

export async function apiExecuteBlock({ header, ...data }, signal) {
  return ApiService.fetchData(
    {
      url: servicePrefix + "/start",
      method: "post",
      data: { ...data },
      headers: {
        "X-CSRFToken": csrftoken,
        ...header,
      },
    },
    signal
  );
}

export async function apiExecuteBackgroundProcess({ header, ...data }) {
  return ApiService.fetchData(
    {
      url: `/engine/studio-processing/${data.projectId}`,
      method: "post",
      data: { ...data },
      headers: {
        "X-CSRFToken": csrftoken,
        ...header,
      },
    },
    signal
  );
}

export async function apiExecutePipeline(data) {
  return ApiService.fetchData({
    url: `/models/pipe/${data?.project_id}/`,
    method: "post",
    data: { ...data },
    headers: {
      "X-CSRFToken": csrftoken,
    },
  });
}
export async function apiExecuteProductionPipeline(data, signal) {
  return ApiService.fetchData(
    {
      url: `/models/production/`,
      method: "post",
      data: { ...data },
      headers: {
        "X-CSRFToken": csrftoken,
      },
    },
    signal
  );
}
export async function apiRunPipeline(data) {
  return ApiService.fetchData({
    url: "/models/production/",
    method: "post",
    data: { ...data },
    headers: {
      "X-CSRFToken": csrftoken,
    },
  });
}
export async function apiGetPipeline(projectId) {
  return ApiService.fetchData({
    url: `/models/pipe/${projectId}/`,
    method: "get",
    // data: { ...data },
    headers: {
      "X-CSRFToken": csrftoken,
    },
  });
}

export async function apiCreateDataset(data) {
  return ApiService.fetchData({
    url: servicePrefix + "/datasets/",
    method: "post",
    data,
  });
}

export async function apiListDatasets() {
  return ApiService.fetchData({
    url: servicePrefix + "/datasets/",
    method: "get",
  });
}
export async function apiGetChart() {
  return ApiService.fetchData({
    url: servicePrefix + "/chart/",
    method: "get",
  });
}

export async function apiUpload(data) {
  return ApiService.fetchData({
    url: servicePrefix + "/upload/",
    method: "post",
    data,
  });
}
export async function apiExportModel(data) {
  return ApiService.fetchData({
    url: servicePrefix + "/datasets/",
    method: "post",
    data,
  });
}

export const channelService = createApi({
  reducerPath: "GenesysChannelApi",
  baseQuery: axiosQuery({
    baseUrl: `${
      getEnvironment(Environment.DEVELOPMENT)
        ? appConfig.apiStaging
        : appConfig.apiPrefix
    }/genesys/customer-service/`,
  }),
  // refetchOnFocus: true,
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "customerServices",
    "customerService",
    "connect",
    "connectInsta",
    "connectEmail",
    "connectCall",
    "connectWhatsApp",
  ],

  endpoints: (build) => ({
    setupWhatsApp: build.mutation({
      query: ({ ...values }) => ({
        url: `whatsapp/connect`,
        method: "post",
        data: { ...values },
      }),
    }),

    getRegisteredFBPages: build.query({
      query: ({ organizationId }) => ({
        url: `${organizationId}/fb/pages/`,
      }),
      providesTags: (data) =>
        data
          ? [
              ...data?.map?.(({ id }) => ({ type: "connect", id })),
              { type: "connect", id: "LIST" },
            ]
          : [{ type: "connect", id: "LIST" }],
    }),

    getRegisteredFBPage: build.query({
      query: ({ organizationId, pageId }) => ({
        url: `${organizationId}/fb/pages/${pageId}`,
      }),
    }),

    reAuthorizeFacebook: build.mutation({
      query: ({ accessToken }) => ({
        url: `fb/access-token`,
        method: "put",
        data: { fb_x_token: accessToken },
      }),
    }),

    getRegisteredInstagramPages: build.query({
      query: ({ organizationId }) => ({
        url: `${organizationId}/insta/pages/`,
      }),
      providesTags: (data) =>
        data
          ? [
              ...data?.map?.(({ id }) => ({ type: "connectInsta", id })),
              { type: "connectInsta", id: "LIST" },
            ]
          : [{ type: "connectInsta", id: "LIST" }],
    }),

    getRegisteredInstagramPage: build.query({
      query: ({ organizationId, pageId }) => ({
        url: `${organizationId}/insta/pages/${pageId}`,
      }),
    }),

    getUsersFBPages: build.query({
      query: ({ accessToken, ...params }) => ({
        baseUri: `https://graph.facebook.com/v18.0/me/accounts`,
        params: { access_token: accessToken, ...params },
      }),
    }),

    deleteFBPage: build.mutation({
      query: ({ pageId }) => ({
        url: `facebook/${pageId}`,
        method: "delete",
      }),
      invalidatesTags: ["connect"],
    }),

    getUsersInstagramPages: build.query({
      query: () => ({
        url: ``,
      }),
    }),

    deleteInstagramPage: build.mutation({
      query: ({ instagramId }) => ({
        url: `instagram/${instagramId}`,
        method: "delete",
      }),
    }),

    createFBConnection: build.mutation({
      query: ({ body }) => ({
        url: `fb/connect`,
        method: "post",
        data: body,
      }),
      invalidatesTags: ["connect"],
    }),

    createInstaConnection: build.mutation({
      query: ({ body }) => ({
        url: `insta/connect`,
        method: "post",
        data: body,
      }),
      invalidatesTags: ["connectInsta"],
    }),

    createEmailConnection: build.mutation({
      query: ({ organization_id, chatbot_id, code }) => ({
        url: `outlook/mails/connect`,
        method: "post",
        data: {
          organization_id,
          chatbot_id,
          code,
        },
      }),
      invalidatesTags: ["connectEmail"],
    }),

    getConnectedEmails: build.query({
      query: ({ organization_id }) => ({
        url: `${organization_id}/outlook/mails`,
      }),
      providesTags: (data) =>
        data
          ? [
              ...data?.map?.(({ id }) => ({ type: "connectEmail", id })),
              { type: "connectEmail", id: "LIST" },
            ]
          : [{ type: "connectEmail", id: "LIST" }],
    }),

    getConnectedEmail: build.query({
      query: ({ mail_id }) => ({
        url: `outlook/mails/${mail_id}`,
      }),
    }),

    changeEmailChatbot: build.mutation({
      query: ({ mailId, chatbot_id }) => ({
        url: `mail/${mailId}`,
        method: "put",
        data: { chatbot_id },
      }),
    }),

    deleteEmail: build.mutation({
      query: ({ mailId }) => ({
        url: `outlook/mails/${mailId}`,
        method: "delete",
      }),
      invalidatesTags: ["connectEmail"],
    }),

    getConnectedPhoneCalls: build.query({
      query: ({ organizationId }) => ({
        url: `${organizationId}/connected/calls`,
      }),
      providesTags: (data) =>
        data
          ? [
              ...data?.map?.(({ id }) => ({ type: "connectCall", id })),
              { type: "connectCall", id: "LIST" },
            ]
          : [{ type: "connectCall", id: "LIST" }],
    }),

    getPhoneCallSessions: build.query({
      query: ({ phoneId }) => ({
        url: `calls/${phoneId}/sessions`,
      }),
    }),

    getPhoneCallSession: build.query({
      query: ({ phoneId, sessionId }) => ({
        url: `calls/${phoneId}/sessions/${sessionId}`,
      }),
    }),

    deletePhoneCall: build.mutation({
      query: ({ callId }) => ({
        url: `call/${callId}`,
        method: "delete",
      }),
      invalidatesTags: ["connectCall"],
    }),

    createRequest: build.mutation({
      query: ({ ...values }) => ({
        url: `calls/requests`,
        method: "post",
        data: { ...values },
      }),
      invalidatesTags: ["connectCall"],
    }),

    changeWhatsAppChatbot: build.mutation({
      query: ({ whatsappId, chatbot_id }) => ({
        url: `whatsapp/${whatsappId}`,
        method: "put",
        data: { chatbot_id },
      }),
    }),

    reAuthorizeWhatsApp: build.mutation({
      query: ({ whatsapp_id }) => ({
        url: `whatsapp/${whatsapp_id}/reconnect`,
        method: "Post",
        data: {},
      }),
      invalidatesTags: ["connectWhatsApp"],
    }),

    getConnectedWhatsApps: build.query({
      query: ({ organizationId }) => ({
        url: `${organizationId}/connected/whatsapp`,
      }),
      providesTags: (data) =>
        data
          ? [
              ...data?.map?.(({ id }) => ({ type: "connectWhatsApp", id })),
              { type: "connectWhatsApp", id: "LIST" },
            ]
          : [{ type: "connectWhatsApp", id: "LIST" }],
    }),

    getConnectedWhatsApp: build.query({
      query: ({ whatsAppId }) => ({
        url: `whatsapp/${whatsAppId}`,
      }),
    }),

    deleteWhatsApp: build.mutation({
      query: ({ whatsappId }) => ({
        url: `whatsapp/${whatsappId}`,
        method: "delete",
      }),
      invalidatesTags: ["connectWhatsApp"],
    }),

    getCustomerServiceLogs: build.query({
      query: ({ id, type = "none", offset, limit }) => ({
        url: `${id}/logs?type=${type}&offset=${offset}`,
      }),
    }),
  }),
});

export const {
  useSetupWhatsAppMutation,
  useChangeEmailChatbotMutation,
  useChangeWhatsAppChatbotMutation,
  useCreateEmailConnectionMutation,
  useCreateFBConnectionMutation,
  useCreateInstaConnectionMutation,
  useCreateRequestMutation,
  useDeleteEmailMutation,
  useDeleteFBPageMutation,
  useDeleteInstagramPageMutation,
  useDeletePhoneCallMutation,
  useDeleteWhatsAppMutation,
  useGetConnectedEmailQuery,
  useGetConnectedEmailsQuery,
  useGetConnectedPhoneCallsQuery,
  useGetConnectedWhatsAppQuery,
  useGetConnectedWhatsAppsQuery,
  useGetPhoneCallSessionQuery,
  useGetPhoneCallSessionsQuery,
  useGetRegisteredFBPageQuery,
  useGetRegisteredFBPagesQuery,
  useGetRegisteredInstagramPageQuery,
  useGetRegisteredInstagramPagesQuery,
  useGetUsersFBPagesQuery,
  useGetUsersInstagramPagesQuery,
  useReAuthorizeWhatsAppMutation,
  useReAuthorizeFacebookMutation,
  useGetCustomerServiceLogsQuery
} = channelService;
